import React, { useEffect } from "react";
import { Button, DatePicker, Form, Input, Select } from "antd";
import type { FormInstance } from "antd";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import styles from "./CoachForm.module.scss";
import { CoachPersonalInfo } from "../../types/coachApplication";

const { Option } = Select;

const capitalizeFirstLetter = (value?: string): string => {
  if (!value) return value ?? "";
  return value.charAt(0).toUpperCase() + value.slice(1);
};  
interface CoachStepTwoProps {
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  initialValues?: CoachPersonalInfo;
  persistToSession?: boolean;
  onSave?: (values: CoachPersonalInfo) => void;
  embedded?: boolean;
  reviewLayout?: boolean;
  form?: FormInstance<CoachStepTwoFormValues>;
}

interface CoachStepTwoFormValues {
  name: string;
  fathersName: string;
  surname: string;
  placeOfBirth: string;
  residence: string;
  dateOfBirth: Dayjs;
  gender: "MALE" | "FEMALE";
  nationality: string;
  personalNumber: string;
  address: string;
}

const MIN_AGE_YEARS = 18;
const MAX_AGE_YEARS = 65;
const LETTERS_MIN3_PATTERN = /^[A-Za-zçÇëËäÄöÖüÜ]{3,}$/;

const cities = [
  "Artanë", "Dardanë", "Deçan", "Sharr", "Drenas", "Ferizaj",
  "Fushë Kosovë", "Gjakovë", "Gjilan", "Graçanicë", "Hani Elezit",
  "Burim", "Junik", "Kaçanik", "Klinë", "Kllokot", "Albanik",
  "Lipjan", "Malishevë", "Mamushë", "Mitrovicë", "Mitrovicë e Veriut",
  "Kastriot", "Partesh", "Pejë", "Besianë", "Prishtinë", "Prizren",
  "Rahovec", "Shtërpcë", "Shtime", "Skënderaj", "Therandë", "Viti",
  "Vushtrri", "Zubin Potok", "Zveçan",
];

const CoachStepTwo: React.FC<CoachStepTwoProps> = ({
  setCurrentStep,
  initialValues,
  persistToSession = true,
  onSave,
  embedded = false,
  reviewLayout = false,
  form: externalForm,
}) => {
  const { t, i18n } = useTranslation();
  const [internalForm] = Form.useForm<CoachStepTwoFormValues>();
  const form = externalForm || internalForm;

  useEffect(() => {
    if (!initialValues) return;
    form.setFieldsValue({
      name: initialValues.name,
      fathersName: initialValues.fathersName,
      surname: initialValues.surname,
      placeOfBirth: initialValues.placeOfBirth,
      residence: initialValues.residence,
      gender: initialValues.gender,
      nationality: initialValues.nationality,
      personalNumber: initialValues.personalNumber,
      address: initialValues.address,
      dateOfBirth: initialValues.dateOfBirth ? dayjs(initialValues.dateOfBirth) : undefined,
    });
  }, [initialValues, form]);
 
useEffect(() => {
  const fields = form.getFieldsError();
  const fieldsWithErrors = fields
    .filter(({ errors }) => errors.length > 0)
    .map(({ name }) => name);

  if (fieldsWithErrors.length > 0) {
    form.validateFields(fieldsWithErrors);
  }
}, [i18n.language, form]);

  const toPersonalInfo = (values: CoachStepTwoFormValues): CoachPersonalInfo => ({
    ...values,
    dateOfBirth: values.dateOfBirth?.format("YYYY-MM-DD"),
  });

  const onFinish = (values: CoachStepTwoFormValues) => {
    const formatted = toPersonalInfo(values);
    if (persistToSession) {
      sessionStorage.setItem("coachStepTwo", JSON.stringify(formatted));
    }
    onSave?.(formatted);
    if (!embedded) {
      setCurrentStep(2);
    }
  };

  return (
      <div className={`${styles.CoachFormContainer} ${embedded || reviewLayout ? styles.CoachFormContainerReview : ""}`}>
        <div className={styles.CoachFormTitle}>
          <h1>{t("app.coaches.stepTwo.title")}</h1>
          <h3>{t("app.coaches.stepTwo.subtitle")}</h3>
        </div>

        <Form
          form={form}
          onFinish={onFinish}
          layout="vertical"
          onValuesChange={(_, allValues) => {
            if (!embedded) return;
            onSave?.(toPersonalInfo(allValues as CoachStepTwoFormValues));
          }}
        >
          <div className={styles.CoachForm}>

            <div className={styles.CoachFormAlignment}>
              <div>
                <p>{t("app.coaches.firstName")}</p>
                <Form.Item
                    name="name"
                    normalize={capitalizeFirstLetter}
                    rules={[
                      { required: true, message: t("app.validations.required") },
                      {
                        pattern: LETTERS_MIN3_PATTERN,
                        message: t("app.coaches.validation.lettersOnly"),
                      },
                    ]}
                >
                  <Input />
                </Form.Item>
              </div>

              <div>
                <p>{t("app.coaches.parentName")}</p>
                <Form.Item
                    name="fathersName"
                    normalize={capitalizeFirstLetter}
                    rules={[
                      { required: true, message: t("app.validations.required") },
                      {
                        pattern: LETTERS_MIN3_PATTERN,
                        message: t("app.coaches.validation.lettersOnly"),
                      },
                    ]}
                >
                  <Input />
                </Form.Item>
              </div>
            </div>

            <div className={styles.CoachFormAlignment}>
              <div>
                <p>{t("app.coaches.lastName")}</p>
                <Form.Item
                    name="surname"
                    normalize={capitalizeFirstLetter}
                    rules={[
                      { required: true, message: t("app.validations.required") },
                      {
                        pattern: LETTERS_MIN3_PATTERN,
                        message: t("app.coaches.validation.lettersOnly"),
                      },
                    ]}
                >
                  <Input />
                </Form.Item>
              </div>

              <div>
                <p>{t("app.coaches.birthPlace")}</p>
                <Form.Item
                    name="placeOfBirth"
                    normalize={capitalizeFirstLetter}
                    rules={[
                      { required: true, message: t("app.validations.required") },
                      {
                        pattern: LETTERS_MIN3_PATTERN,
                        message: t("app.coaches.validation.lettersOnly"),
                      },
                    ]}
                >
                  <Input />
                </Form.Item>
              </div>
            </div>

            <div className={styles.CoachFormAlignment}>
              <div>
                <p>{t("app.coaches.birthCity")}</p>
                <Form.Item
                    name="residence"
                    normalize={capitalizeFirstLetter}
                    validateFirst
                    rules={[
                      {
                        required: true,
                        message: t("app.coaches.validation.residenceRequired"),
                      },
                      {
                        validator: (_, value) => {
                          if (!value || !value.trim()) {
                            return Promise.resolve();
                          }

                          const trimmedValue = value.trim();

                          if (trimmedValue.length < 2) {
                            return Promise.reject(
                                new Error(
                                    t("app.coaches.validation.residenceMin")
                                )
                            );
                          }

                          if (trimmedValue.length > 50) {
                            return Promise.reject(
                                new Error(
                                    t("app.coaches.validation.residenceMax")
                                )
                            );
                          }

                          if (!/^[\p{L}\p{M}\s'.,-]+$/u.test(trimmedValue)) {
                            return Promise.reject(
                                new Error(t("app.coaches.validation.residenceInvalid"))
                            );
                          }

                          return Promise.resolve();
                        },
                      },
                    ]}
                >
                  <Input
                      // placeholder="Enter your place of residence"
                      maxLength={50}
                  />
                </Form.Item>
              </div>

              <div>
                <p>{t("app.coaches.birthDate")}</p>
                <Form.Item
                    name="dateOfBirth"
                    rules={[
                      { required: true, message: t("app.validations.required") },
                      {
                        validator: (_, value: Dayjs | undefined) => {
                          if (!value) return Promise.resolve(); // "required" rule already covers empty
                          const age = dayjs().diff(value, "year");
                          if (age < MIN_AGE_YEARS || age > MAX_AGE_YEARS) {
                            return Promise.reject(
                                new Error(
                                    t("app.coaches.validation.ageRange", {
                                      min: MIN_AGE_YEARS,
                                      max: MAX_AGE_YEARS,
                                    })
                                )
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                >
                  <DatePicker
                      style={{ width: "100%" }}
                      format="DD/MM/YYYY"
                      placeholder="DD/MM/YYYY"
                      disabledDate={(current) => {
                        if (!current) return false;
                        const age = dayjs().diff(current, "year");
                        return age < MIN_AGE_YEARS || age > MAX_AGE_YEARS;
                      }}
                      defaultPickerValue={dayjs().subtract(MIN_AGE_YEARS, "year")}
                  />
                </Form.Item>
              </div>
            </div>

            <div className={styles.CoachFormAlignment}>
              <div>
                <p>{t("app.coaches.gender")}</p>
                <Form.Item
                    name="gender"
                    rules={[
                      { required: true, message: t("app.validations.required") },
                    ]}
                >
                  <Select placeholder={t("app.coaches.selectGender")}>
                    <Option value="MALE">{t("app.coaches.gender.male")}</Option>
                    <Option value="FEMALE">{t("app.coaches.gender.female")}</Option>
                  </Select>
                </Form.Item>
              </div>

              <div>
                <p>{t("app.coaches.nationality")}</p>
                <Form.Item
                    name="nationality"
                    normalize={capitalizeFirstLetter}
                    rules={[
                      { required: true, message: t("app.validations.required") },
                      {
                        pattern: LETTERS_MIN3_PATTERN,
                        message: t("app.coaches.validation.lettersOnly"),
                      },
                    ]}
                >
                  <Input />
                </Form.Item>
              </div>
            </div>

            <div className={styles.CoachFormAlignment}>
              <div>
                <p>{t("app.coaches.personalNumber")}</p>
                <Form.Item
                    name="personalNumber"
                    rules={[
                      { required: true, message: t("app.validations.required") },
                      {
                        pattern: /^\d{10}$/,
                        message: t("app.volunteers.validation.personalNumber"),
                      },
                      {
                        pattern: /^(?!([0-9])\1{9}$)(?!0123456789$)(?!1234567890$)[0-9]{10}$/,
                        message: t("app.coaches.validation.personalNumberInvalid"),
                      },
                    ]}
                >
                  <Input maxLength={10} />
                </Form.Item>
              </div>

              <div>
                <p>{t("app.coaches.address")}</p>
                <Form.Item
                    name="address"
                    normalize={capitalizeFirstLetter}
                    rules={[
                      { required: true, message: t("app.validations.required") },
                      {
                        min: 5,
                        message: t("app.coaches.validation.address"),
                      },
                    ]}
                >
                  <Input />
                </Form.Item>
              </div>
            </div>

          </div>

          {!embedded && (
            <div className={styles.ButtonAlignment}>
              <Button type="primary" htmlType="submit" className={styles.ButtonStyle}>
                {t("app.coaches.continue")}
              </Button>
            </div>
          )}
        </Form>
      </div>
  );
};

export default CoachStepTwo;